import React, { useEffect } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  Typography,
  createTheme
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import colors from "../../../../shared/themes/colors";
import { enumToArray } from "../../../../shared/models/models";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { integerRegex, twoDecimalsRegex } from "../../../../shared/validators";
import Loader from "../../../../shared/components/Loader";
import { ProductConditionCode, productCondition } from "../../../../common/models/CommonModels";
import { getCategories, getManufacturers, setLoading } from "../../customerMyRfqsSlice";
import { ChangeRfq, CustomerMyRfqsState } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";

const ProductDetailsStep = () => {
  const { manufacturers, categories, loading } = useAppSelector<CustomerMyRfqsState>(
    (state: any) => state.customerMyRfqs
  );

  const dispatch = useAppDispatch();

  const {
    register,
    trigger,
    formState: { errors }
  } = useFormContext<ChangeRfq>();

  const validateProductConditionCodes = (value: ProductConditionCode[]): string | boolean => {
    return !value?.length ? "At least one condition is required" : true;
  };

  const init = async () => {
    dispatch(setLoading(true));
    await dispatch(getManufacturers());
    await dispatch(getCategories());
    dispatch(setLoading(false));
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: colors.secondary.gray70
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: colors.secondary.gray70,
            fontWeight: 500
          }
        }
      }
    }
  });

  const { translate } = useTranslation("common");

  if (loading) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box className="mt-5">
        <Typography fontSize={36} fontWeight={500} className="heading-1">
          Create a new RFQ
        </Typography>
      </Box>

      <Box className="mt-5">
        <Typography fontSize={24} fontWeight={500} className="heading-2">
          Product details
        </Typography>
      </Box>

      <Box className="mt-4 grid grid-cols-2 gap-4">
        <Box>
          <InputLabel required>Product number</InputLabel>
          <TextField
            className="w-full"
            {...register("productNumber", { required: "Field is required" })}
            error={!!errors.productNumber}
            helperText={errors.productNumber?.message}
          />
        </Box>

        <Box>
          <InputLabel>Alternative number</InputLabel>
          <TextField
            className="w-full"
            {...register("productAlternativeNumber")}
            error={!!errors.productAlternativeNumber}
            helperText={errors.productAlternativeNumber?.message}
          />
        </Box>
      </Box>

      <Box className="mt-4 grid grid-cols-2 gap-4">
        <Box>
          <InputLabel required>Brand</InputLabel>
          <FormControl className="w-full">
            <Select
              key="manufacturerId"
              defaultValue={""}
              onClose={() => setTimeout(() => trigger("manufacturerId"))}
              {...register("manufacturerId", { required: "Field is required" })}
              error={!!errors.manufacturerId}
            >
              {manufacturers.map((manufacturer) => (
                <MenuItem key={manufacturer.id} value={manufacturer.id}>
                  {manufacturer.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText error>{errors.manufacturerId?.message}</FormHelperText>
          </FormControl>
        </Box>

        <Box>
          <InputLabel required>Category</InputLabel>
          <FormControl className="w-full">
            <Select
              key="categoryId"
              defaultValue={""}
              onClose={() => setTimeout(() => trigger("categoryId"))}
              {...register("categoryId", { required: "Field is required" })}
              error={!!errors.categoryId}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText error>{errors.categoryId?.message}</FormHelperText>
          </FormControl>
        </Box>
      </Box>

      <Box className="mt-4 grid grid-cols-2 gap-4">
        <Box>
          <InputLabel>Price, €</InputLabel>
          <TextField
            className="w-full"
            type="number"
            {...register("maxUnitPrice", {
              pattern: { value: twoDecimalsRegex, message: "Invalid value" },
              min: { value: 0.01, message: "Price must be at least 0.01" }
            })}
            error={!!errors.maxUnitPrice}
            helperText={errors.maxUnitPrice?.message}
          />
        </Box>

        <Box>
          <InputLabel required>Quantity</InputLabel>
          <TextField
            className="w-full"
            type="number"
            {...register("quantity", {
              required: "Field is required",
              pattern: { value: integerRegex, message: "Invalid value" },
              min: { value: 1, message: "Quantity must be at least 1" }
            })}
            error={!!errors.quantity}
            helperText={errors.quantity?.message}
          />
        </Box>
      </Box>

      <Box className="mt-4">
        <InputLabel>Condition</InputLabel>
        <FormControl>
          <Box className="flex gap-1">
            {enumToArray<ProductConditionCode>(ProductConditionCode).map((code) => (
              <FormControlLabel
                key={code}
                label={translate(`productCondition.${productCondition(code)}`)}
                control={
                  <Checkbox
                    value={code}
                    defaultChecked={true}
                    {...register("productConditionCodes", { validate: validateProductConditionCodes })}
                  />
                }
              />
            ))}
          </Box>

          <FormHelperText error>{errors.productConditionCodes?.message}</FormHelperText>
        </FormControl>
      </Box>

      <Box className="mt-4">
        <InputLabel>Product description</InputLabel>
        <TextField
          className="w-full textarea"
          multiline
          rows={3}
          {...register("description")}
          error={!!errors.description}
          helperText={errors.description?.message}
        />
      </Box>
    </ThemeProvider>
  );
};

export default ProductDetailsStep;
